<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import {
  DEFAULT_INPUTS,
  MAX_SIMULATION_MINUTES,
  PID_TUNING,
  SPACE_TYPE_OPTIONS,
  TEMPERATURE_TARGET,
  assessComfort,
  calculateDesignAirflows,
  calculateFanFloor,
  calculateStability,
  createInitialSimulationState,
  recommendWaterSourceMode,
  stepComfortSimulation,
  toSimulationPoint,
  type SimulationInputs,
  type SimulationPoint
} from '../simulation/comfortEngine'

const inputs = reactive<SimulationInputs>({ ...DEFAULT_INPUTS })
const state = ref(createInitialSimulationState(inputs))
const history = ref<SimulationPoint[]>([toSimulationPoint(0, state.value)])
const simTime = ref(0)
const running = ref(false)
const speed = ref<1 | 5 | 20>(5)
let timer: number | undefined
let inputRestartTimer: number | undefined
const autoTuning = ref(false)

type PidGainKey = 'pidKp' | 'pidKi' | 'pidKd'
const PID_GAIN_LIMITS: Record<PidGainKey, { min: number; max: number; fallback: number }> = {
  pidKp: { min: 0, max: 1, fallback: DEFAULT_INPUTS.pidKp },
  pidKi: { min: 0, max: 0.2, fallback: DEFAULT_INPUTS.pidKi },
  pidKd: { min: 0, max: 0.5, fallback: DEFAULT_INPUTS.pidKd }
}

const latest = computed(() => history.value[history.value.length - 1] ?? toSimulationPoint(0, state.value))
const stability = computed(() => calculateStability(history.value, simTime.value))
const modeLabel = computed(() => {
  if (!inputs.acEnabled) return '停机'
  if (state.value.mode === 'cooling') return '制冷调节'
  if (state.value.mode === 'heating') return '制热调节'
  return '恒温保持'
})
const comfortAssessment = computed(() => assessComfort(latest.value.indoorTemp, latest.value.co2, latest.value.humidity))
const comfortMeta = computed(() => {
  const score = latest.value.comfort
  if (score >= 90) return { key: 'comfortable', label: '舒适', color: '#43e2a8' }
  if (score >= 75) return { key: 'good', label: '良好', color: '#29d8e8' }
  if (score >= 60) return { key: 'fair', label: '一般', color: '#ffae4a' }
  if (score >= 40) return { key: 'improve', label: '需改善', color: '#ff8a5b' }
  return { key: 'uncomfortable', label: '不舒适', color: '#ff6678' }
})
const roomState = computed(() => comfortMeta.value.label)
const comfortFactors = computed(() => [
  { key: 'temperature', label: '温度', score: comfortAssessment.value.temperatureScore },
  { key: 'airQuality', label: 'CO₂', score: comfortAssessment.value.airQualityScore },
  { key: 'humidity', label: '湿度', score: comfortAssessment.value.humidityScore }
])
const weakestComfortFactor = computed(() => comfortFactors.value.reduce((weakest, factor) => factor.score < weakest.score ? factor : weakest))
const comfortIssue = computed(() => {
  if (latest.value.comfort >= 98) return '环境接近理想状态'
  if (weakestComfortFactor.value.key === 'temperature') return latest.value.indoorTemp >= 24 ? '主要影响：室温偏高' : '主要影响：室温偏低'
  if (weakestComfortFactor.value.key === 'airQuality') return '主要影响：CO₂ 偏高'
  return latest.value.humidity >= 50 ? '主要影响：湿度偏高' : '主要影响：湿度偏低'
})
const people = computed(() => Array.from({ length: Math.min(4, Math.ceil(inputs.occupancy / 10)) }, (_, index) => index))
const airflowSpeed = computed(() => `${Math.max(0.55, 2.2 - state.value.fanFrequency / 35).toFixed(2)}s`)
const designAirflows = computed(() => calculateDesignAirflows(inputs))
const supplyPercentage = computed(() => designAirflows.value.maximumSupplyFlow > 0
  ? Math.min(100, Math.max(0, state.value.supplyFlow / designAirflows.value.maximumSupplyFlow * 100))
  : 0)
const pidPercentage = computed(() => Math.abs(state.value.pidOutput) * 100)
const pidOutputLabel = computed(() => `${state.value.pidOutput >= 0 ? '+' : ''}${(state.value.pidOutput * 100).toFixed(1)}%`)
const pidError = computed(() => state.value.indoorTemp - inputs.setpoint)
const pidErrorLabel = computed(() => `${pidError.value >= 0 ? '+' : ''}${pidError.value.toFixed(2)}℃`)
const fanFloor = computed(() => calculateFanFloor(inputs))
const waterSourceLabel = computed(() => inputs.waterSourceMode === 'cooling' ? '冷水' : '热水')
const trend = computed(() => history.value)

function dynamicScale(key: 'indoorTemp' | 'co2', minimumSpan: number) {
  const values = trend.value.map((point) => point[key])
  const rawMin = Math.min(...values)
  const rawMax = Math.max(...values)
  const center = (rawMin + rawMax) / 2
  const span = Math.max(minimumSpan, rawMax - rawMin)
  return { min: center - span * 0.62, max: center + span * 0.62 }
}

const temperatureScale = computed(() => {
  const values = [...trend.value.map((point) => point.indoorTemp), inputs.setpoint]
  const rawMin = Math.min(...values)
  const rawMax = Math.max(...values)
  const center = (rawMin + rawMax) / 2
  const span = Math.max(0.4, rawMax - rawMin)
  return { min: center - span * 0.62, max: center + span * 0.62 }
})
const co2Scale = computed(() => dynamicScale('co2', 4))
const trendTimeRange = computed(() => ({
  start: trend.value[0]?.t ?? 0,
  end: trend.value[trend.value.length - 1]?.t ?? 0
}))

function chartPoints(key: 'indoorTemp' | 'co2', min: number, max: number) {
  const points = trend.value
  if (points.length < 2) return '0,70 100,70'
  const startTime = points[0]?.t ?? 0
  const endTime = points[points.length - 1]?.t ?? startTime + 1
  const timeSpan = Math.max(0.1, endTime - startTime)
  return points.map((point) => {
    const x = ((point.t - startTime) / timeSpan) * 100
    const y = 74 - ((point[key] - min) / (max - min)) * 62
    return `${x.toFixed(2)},${Math.max(5, Math.min(74, y)).toFixed(2)}`
  }).join(' ')
}

function stopTimer() {
  if (timer !== undefined) window.clearInterval(timer)
  timer = undefined
}

function clearInputRestartTimer() {
  if (inputRestartTimer !== undefined) window.clearTimeout(inputRestartTimer)
  inputRestartTimer = undefined
}

function tick() {
  const dtMinutes = 0.05 * speed.value
  state.value = stepComfortSimulation(state.value, inputs, dtMinutes)
  simTime.value = Math.min(MAX_SIMULATION_MINUTES, simTime.value + dtMinutes)
  history.value = [...history.value, toSimulationPoint(simTime.value, state.value)].slice(-2000)
  if (simTime.value >= MAX_SIMULATION_MINUTES) pause()
}

function start() {
  if (running.value) return
  running.value = true
  stopTimer()
  timer = window.setInterval(tick, 200)
}

function pause() {
  running.value = false
  stopTimer()
}

function toggleRunning() {
  if (running.value) pause()
  else start()
}

function reset() {
  pause()
  state.value = createInitialSimulationState(inputs)
  simTime.value = 0
  history.value = [toSimulationPoint(0, state.value)]
}

function updatePidValue(key: PidGainKey, event: Event) {
  const target = event.target
  if (!(target instanceof HTMLInputElement) || target.value.trim() === '') return
  const range = PID_GAIN_LIMITS[key]
  const value = Number(target.value)
  if (!Number.isFinite(value)) return
  inputs[key] = Math.min(range.max, Math.max(range.min, value))
}

function normalizePidValue(key: PidGainKey, event: Event) {
  const target = event.target
  if (!(target instanceof HTMLInputElement)) return
  const range = PID_GAIN_LIMITS[key]
  const value = Number(target.value)
  inputs[key] = Number.isFinite(value)
    ? Math.min(range.max, Math.max(range.min, value))
    : range.fallback
}

watch(
  () => [inputs.outdoorTemp, inputs.setpoint] as const,
  ([outdoorTemp, setpoint]) => {
    inputs.waterSourceMode = recommendWaterSourceMode(
      outdoorTemp,
      setpoint,
      inputs.waterSourceMode
    )
  },
  { immediate: true }
)

watch(
  () => [
    inputs.outdoorTemp,
    inputs.occupancy,
    inputs.area,
    inputs.height,
    inputs.spaceType,
    inputs.acEnabled,
    inputs.setpoint,
    inputs.pidKp,
    inputs.pidKi,
    inputs.pidKd,
    inputs.waterSourceMode
  ],
  () => {
    autoTuning.value = true
    clearInputRestartTimer()
    if (simTime.value >= MAX_SIMULATION_MINUTES) {
      state.value = createInitialSimulationState(inputs)
      simTime.value = 0
      history.value = [toSimulationPoint(0, state.value)]
    }
    start()
    inputRestartTimer = window.setTimeout(() => {
      autoTuning.value = false
    }, 650)
  }
)

onBeforeUnmount(() => {
  stopTimer()
  clearInputRestartTimer()
})
</script>

<template>
  <section class="simulation-page" :class="[`mode-${state.mode}`, { 'is-running': running }]" :style="{ '--comfort-color': comfortMeta.color }">
    <header class="simulation-heading">
      <div>
        <p class="eyebrow">BA DIGITAL TWIN / COMFORT ENGINE</p>
        <h2>新风空调 · 3D 仿真展厅</h2>
        <p>将空调主机设备区与展厅房间区分开建模，通过送风、回风和 DDC 控制形成同一仿真闭环。</p>
      </div>
      <div class="simulation-runtime" aria-label="仿真运行控制">
        <span><i :class="{ active: running || autoTuning }"></i>{{ autoTuning ? '输入变化，重新计算' : running ? '实时计算中' : '仿真已暂停' }}</span>
        <strong>SIM {{ simTime.toFixed(1) }} min</strong>
        <div class="simulation-speed" role="group" aria-label="仿真速度">
          <button v-for="item in ([1, 5, 20] as const)" :key="item" type="button" :class="{ active: speed === item }" :aria-pressed="speed === item" @click="speed = item">{{ item }}×</button>
        </div>
        <button class="simulation-run" type="button" @click="toggleRunning">{{ running ? '暂停' : '开始演示' }}</button>
        <button class="simulation-reset" type="button" @click="reset">重置</button>
      </div>
    </header>

    <div class="simulation-layout">
      <aside class="simulation-controls">
        <div class="simulation-panel-title"><span>01</span><div><strong>场景配置</strong><small>CONFIGURATION LAYER</small></div></div>

        <label class="simulation-slider">
          <span>室外温度 <b>{{ inputs.outdoorTemp.toFixed(1) }}℃</b></span>
          <input v-model.number="inputs.outdoorTemp" type="range" min="-20" max="42" step="0.5" :aria-valuetext="`${inputs.outdoorTemp.toFixed(1)} 摄氏度`" />
        </label>
        <label class="simulation-slider">
          <span>展厅人数 <b>{{ inputs.occupancy }} 人</b></span>
          <input v-model.number="inputs.occupancy" type="range" min="0" max="50" step="1" :aria-valuetext="`${inputs.occupancy} 人`" />
        </label>
        <label class="simulation-slider">
          <span>房间面积 <b>{{ inputs.area }}㎡</b></span>
          <input v-model.number="inputs.area" type="range" min="10" max="200" step="5" :aria-valuetext="`${inputs.area} 平方米`" />
        </label>
        <label class="simulation-slider">
          <span>房间层高 <b>{{ inputs.height.toFixed(1) }}m</b></span>
          <input v-model.number="inputs.height" type="range" min="2.5" max="5" step="0.1" :aria-valuetext="`${inputs.height.toFixed(1)} 米`" />
        </label>
        <div class="simulation-control-divider simulation-changeover">
          <span>房间使用场景</span>
          <select v-model="inputs.spaceType" aria-label="房间使用场景">
            <option v-for="option in SPACE_TYPE_OPTIONS" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>

        <div class="simulation-control-divider"><span>AHU 运行许可</span><label><input v-model="inputs.acEnabled" type="checkbox" aria-label="AHU 运行许可" />{{ inputs.acEnabled ? '运行' : '停机' }}</label></div>
        <div class="simulation-control-divider simulation-changeover">
          <span>两管制冷热源许可 <small>随室外温度自动切换</small></span>
          <select v-model="inputs.waterSourceMode" :disabled="!inputs.acEnabled" aria-label="两管制冷热源许可">
            <option value="cooling">制冷水许可</option>
            <option value="heating">制热水许可</option>
          </select>
        </div>
        <label class="simulation-slider" :class="{ disabled: !inputs.acEnabled }">
          <span>温度设定值 <b>{{ inputs.setpoint.toFixed(1) }}℃</b></span>
          <input v-model.number="inputs.setpoint" type="range" min="18" max="28" step="0.5" :disabled="!inputs.acEnabled" :aria-valuetext="`${inputs.setpoint.toFixed(1)} 摄氏度`" />
        </label>
        <div class="room-specs">
          <span>展厅体积</span><strong>{{ Math.round(inputs.area * inputs.height) }}m³</strong>
          <span>人员密度</span><strong>{{ (inputs.occupancy / inputs.area).toFixed(2) }}人/㎡</strong>
          <span>计算新风量</span><strong>{{ designAirflows.outdoorAirFlow }}m³/h</strong>
          <span>设计总送风</span><strong>{{ designAirflows.maximumSupplyFlow }}m³/h</strong>
          <span>设计显热负荷</span><strong>{{ designAirflows.designSensibleLoadKw.toFixed(1) }}kW</strong>
          <span>系统模式</span><strong>{{ modeLabel }}</strong>
        </div>
        <p class="simulation-disclaimer">新风按人数项＋面积项自动计算，总送风按显热负荷与最小换气次数校核；演示结果不替代施工图设计。</p>

      </aside>

      <main class="simulation-stage-column">
        <section class="simulation-room-card" aria-label="空调主机设备区与3D房间联动仿真">
          <header>
            <div><span>02 / DIGITAL SHOWROOM</span><strong>空调主机与房间联动场景</strong></div>
            <div class="room-live-state"><i></i>{{ roomState }} · {{ modeLabel }}</div>
          </header>

          <div class="simulation-room-stage" :style="{ '--airflow-duration': airflowSpeed }">
            <div class="front-perspective-room">
              <svg class="front-room-model" viewBox="0 0 1000 430" preserveAspectRatio="xMidYMid meet" role="img" aria-label="正视单点透视的新风空调3D房间">
                <defs>
                  <linearGradient id="front-room-back" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stop-color="#12475b" />
                    <stop offset="0.52" stop-color="#0a3042" />
                    <stop offset="1" stop-color="#041b2a" />
                  </linearGradient>
                  <linearGradient id="front-room-floor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stop-color="#0c3b4c" />
                    <stop offset="0.36" stop-color="#082c3c" />
                    <stop offset="1" stop-color="#03131f" />
                  </linearGradient>
                  <linearGradient id="front-ahu-face" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stop-color="#378ea0" />
                    <stop offset="0.14" stop-color="#23758a" />
                    <stop offset="0.55" stop-color="#155568" />
                    <stop offset="1" stop-color="#0a3548" />
                  </linearGradient>
                  <linearGradient id="front-ahu-top" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stop-color="#65bfca" />
                    <stop offset="0.38" stop-color="#3d91a2" />
                    <stop offset="1" stop-color="#1c5f73" />
                  </linearGradient>
                  <linearGradient id="front-ahu-side" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stop-color="#123f52" />
                    <stop offset="1" stop-color="#051d2b" />
                  </linearGradient>
                  <linearGradient id="front-supply-metal" x1="0" y1="178" x2="0" y2="207" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stop-color="#62d9e4" />
                    <stop offset="0.22" stop-color="#2b9eb4" />
                    <stop offset="0.68" stop-color="#176b82" />
                    <stop offset="1" stop-color="#0c4055" />
                  </linearGradient>
                  <linearGradient id="front-return-metal" x1="0" y1="232" x2="0" y2="259" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stop-color="#d6a46a" />
                    <stop offset="0.3" stop-color="#9c7244" />
                    <stop offset="1" stop-color="#4c3425" />
                  </linearGradient>
                  <linearGradient id="front-partition-face" x1="512" y1="0" x2="522" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stop-color="#061b28" />
                    <stop offset="0.45" stop-color="#154c5d" />
                    <stop offset="1" stop-color="#082633" />
                  </linearGradient>
                  <linearGradient id="front-window-glass" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stop-color="#174d68" />
                    <stop offset="0.5" stop-color="#0a2c44" />
                    <stop offset="1" stop-color="#061c31" />
                  </linearGradient>
                  <radialGradient id="front-equipment-light-pool">
                    <stop offset="0" stop-color="#ffd49b" stop-opacity="0.11" />
                    <stop offset="1" stop-color="#ffd49b" stop-opacity="0" />
                  </radialGradient>
                  <radialGradient id="front-room-light-pool">
                    <stop offset="0" stop-color="#8ce9f1" stop-opacity="0.13" />
                    <stop offset="1" stop-color="#8ce9f1" stop-opacity="0" />
                  </radialGradient>
                  <pattern id="front-back-grid" width="48" height="42" patternUnits="userSpaceOnUse">
                    <path d="M48 0H0V42" fill="none" stroke="rgba(93, 190, 212, 0.10)" stroke-width="1" />
                  </pattern>
                  <pattern id="front-equipment-panels" width="46" height="220" patternUnits="userSpaceOnUse">
                    <path d="M46 0V220" fill="none" stroke="rgba(172, 220, 228, 0.11)" stroke-width="1" />
                  </pattern>
                  <pattern id="front-equipment-floor-dots" width="13" height="13" patternUnits="userSpaceOnUse">
                    <circle cx="2" cy="2" r="0.9" fill="rgba(172, 220, 228, 0.13)" />
                  </pattern>
                  <filter id="front-ahu-shadow" x="-20%" y="-30%" width="150%" height="180%">
                    <feDropShadow dx="10" dy="13" stdDeviation="8" flood-color="#000b12" flood-opacity="0.52" />
                  </filter>
                  <filter id="front-contact-shadow" x="-20%" y="-100%" width="140%" height="300%">
                    <feGaussianBlur stdDeviation="3" />
                  </filter>
                  <marker id="front-supply-arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto" markerUnits="userSpaceOnUse">
                    <path d="M0 0L8 4L0 8Z" fill="#8af2f4" />
                  </marker>
                  <marker id="front-return-arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto" markerUnits="userSpaceOnUse">
                    <path d="M0 0L8 4L0 8Z" fill="#f1bd78" />
                  </marker>
                  <clipPath id="front-floor-clip">
                    <polygon points="236,300 764,300 955,404 45,404" />
                  </clipPath>
                  <clipPath id="equipment-floor-clip">
                    <polygon points="236,300 512,300 520,404 45,404" />
                  </clipPath>
                  <clipPath id="conditioned-floor-clip">
                    <polygon points="512,300 764,300 955,404 520,404" />
                  </clipPath>
                  <mask id="zone-partition-mask">
                    <rect x="0" y="0" width="1000" height="430" fill="white" />
                    <rect x="499" y="180" width="34" height="25" rx="4" fill="black" />
                    <rect x="499" y="235" width="34" height="23" rx="4" fill="black" />
                  </mask>
                </defs>

                <g class="front-room-shell">
                  <polygon class="front-room-ceiling" points="45,22 955,22 764,78 236,78" />
                  <polygon class="front-room-left-wall" points="45,22 236,78 236,300 45,404" />
                  <polygon class="front-room-right-wall" points="764,78 955,22 955,404 764,300" />
                  <rect class="front-room-back-wall" x="236" y="78" width="528" height="222" />
                  <rect x="236" y="78" width="528" height="222" fill="url(#front-back-grid)" />
                  <polygon class="front-room-floor" points="236,300 764,300 955,404 45,404" />
                  <rect class="front-equipment-zone-tint" x="236" y="78" width="276" height="222" />
                  <polygon class="front-equipment-zone-tint" points="45,22 520,22 512,78 236,78" />
                  <polygon class="front-equipment-zone-tint" points="236,300 512,300 520,404 45,404" />
                  <rect class="front-conditioned-zone-tint" x="512" y="78" width="252" height="222" />
                  <polygon class="front-conditioned-zone-tint" points="520,22 955,22 764,78 512,78" />
                  <polygon class="front-conditioned-zone-tint" points="512,300 764,300 955,404 520,404" />
                  <rect class="front-equipment-wall-texture" x="236" y="78" width="276" height="222" fill="url(#front-equipment-panels)" />
                  <rect class="front-equipment-floor-texture" x="45" y="300" width="475" height="104" clip-path="url(#equipment-floor-clip)" fill="url(#front-equipment-floor-dots)" />
                  <ellipse class="front-equipment-light-pool" cx="330" cy="348" rx="225" ry="54" clip-path="url(#equipment-floor-clip)" />
                  <ellipse class="front-conditioned-light-pool" cx="720" cy="344" rx="215" ry="62" clip-path="url(#conditioned-floor-clip)" />

                  <g class="front-room-floor-grid" clip-path="url(#front-floor-clip)">
                    <line x1="500" y1="156" x2="80" y2="404" /><line x1="500" y1="156" x2="220" y2="404" />
                    <line x1="500" y1="156" x2="360" y2="404" /><line x1="500" y1="156" x2="500" y2="404" />
                    <line x1="500" y1="156" x2="640" y2="404" /><line x1="500" y1="156" x2="780" y2="404" />
                    <line x1="500" y1="156" x2="920" y2="404" />
                    <line x1="199" y1="320" x2="801" y2="320" /><line x1="163" y1="340" x2="837" y2="340" />
                    <line x1="117" y1="365" x2="883" y2="365" /><line x1="71" y1="390" x2="929" y2="390" />
                  </g>
                  <path class="front-room-wall-base-shadow" d="M236 300H764" />
                  <path class="front-room-partition-base-shadow" d="M512 300L520 404" />

                  <g class="front-room-outline">
                    <rect x="45" y="22" width="910" height="382" />
                    <rect x="236" y="78" width="528" height="222" />
                    <line x1="45" y1="22" x2="236" y2="78" /><line x1="955" y1="22" x2="764" y2="78" />
                    <line x1="45" y1="404" x2="236" y2="300" /><line x1="955" y1="404" x2="764" y2="300" />
                  </g>

                  <g class="front-room-lights">
                    <polygon points="235,42 330,42 342,49 247,49" />
                    <polygon points="433,42 493,42 500,49 440,49" /><polygon points="532,42 592,42 585,49 525,49" />
                    <polygon points="670,42 765,42 753,49 658,49" />
                  </g>

                  <g class="front-room-door">
                    <polygon points="78,151 142,169 142,292 78,345" />
                    <line x1="132" y1="184" x2="132" y2="288" />
                    <circle cx="124" cy="245" r="3" />
                    <text x="87" y="270">检修门</text>
                  </g>
                  <g class="front-zone-label front-zone-label-equipment">
                    <rect x="247" y="86" width="119" height="22" rx="3" />
                    <text x="258" y="101">A · 空调主机区</text>
                  </g>
                  <g class="front-zone-label front-zone-label-room">
                    <rect x="526" y="86" width="118" height="22" rx="3" />
                    <text x="537" y="101">B · 房间模型区</text>
                  </g>
                </g>

                <g class="front-room-back-fixtures">
                  <g class="front-room-window">
                    <rect x="535" y="116" width="93" height="57" rx="2" />
                    <line x1="582" y1="116" x2="582" y2="173" /><line x1="535" y1="145" x2="628" y2="145" />
                    <path class="front-room-window-reflection" d="M544 119H568L544 170H536Z" />
                    <text x="546" y="164">展厅外窗</text>
                  </g>
                  <g class="front-room-intake">
                    <rect x="370" y="116" width="120" height="57" rx="2" />
                    <line v-for="line in 6" :key="line" x1="382" :y1="116 + line * 8" x2="478" :y2="116 + line * 8" />
                    <text x="381" y="164">室外新风 {{ Math.round(state.outdoorAirFlow) }}m³/h</text>
                  </g>
                  <g class="front-room-comfort">
                    <rect x="660" y="112" width="86" height="66" rx="3" />
                    <text x="673" y="129">COMFORT</text>
                    <text class="front-room-comfort-value" x="673" y="160">{{ Math.round(latest.comfort) }}</text>
                    <text x="724" y="160">/100</text>
                    <line class="front-room-comfort-bar" x1="668" y1="170" x2="738" y2="170" />
                    <line class="front-room-comfort-progress" x1="668" y1="170" :x2="668 + latest.comfort * 0.7" y2="170" />
                  </g>
                </g>

                <ellipse class="front-room-air-zone" cx="710" cy="339" rx="175" ry="50" clip-path="url(#conditioned-floor-clip)" :style="{ opacity: Math.min(0.55, Math.abs(latest.indoorTemp - inputs.setpoint) / 8 + 0.12) }" />
                <ellipse class="front-ahu-contact-shadow" cx="332" cy="347" rx="150" ry="10" clip-path="url(#equipment-floor-clip)" filter="url(#front-contact-shadow)" />

                <g class="front-room-ducts">
                  <g class="front-room-duct-hangers">
                    <path d="M642 179V187M634 179H650" /><rect x="633" y="187" width="18" height="5" rx="2" />
                    <path d="M742 179V187M734 179H750" /><rect x="733" y="187" width="18" height="5" rx="2" />
                  </g>
                  <path class="front-room-supply-shadow" d="M484 244V192H750" />
                  <path class="front-room-supply-path" d="M484 244V192H750" />
                  <path class="front-room-supply-highlight" d="M484 240V188H750" />
                  <path class="front-room-supply-direction" d="M672 192H724" marker-end="url(#front-supply-arrow)" />
                  <text class="front-room-duct-label supply" x="575" y="181">温控送风 {{ Math.round(state.supplyFlow) }} m³/h</text>
                  <line class="front-room-supply-branch" x1="600" y1="192" x2="600" y2="221" />
                  <line class="front-room-supply-branch" x1="675" y1="192" x2="675" y2="221" />
                  <line class="front-room-supply-branch" x1="735" y1="192" x2="735" y2="221" />
                  <rect class="front-room-diffuser" x="578" y="219" width="44" height="8" rx="2" />
                  <rect class="front-room-diffuser" x="653" y="219" width="44" height="8" rx="2" />
                  <rect class="front-room-diffuser" x="713" y="219" width="44" height="8" rx="2" />
                  <circle v-for="dot in 7" :key="`supply-${dot}`" class="front-room-supply-dot" cx="500" cy="192" r="3" :style="{ '--dot-index': dot }" />

                  <path class="front-room-return-shadow" d="M484 267V245H715" />
                  <path class="front-room-return-path" d="M484 267V245H715" />
                  <path class="front-room-return-highlight" d="M484 264V242H715" />
                  <path class="front-room-return-direction" d="M690 245H642" marker-end="url(#front-return-arrow)" />
                  <text class="front-room-duct-label return" x="625" y="239">回风</text>
                  <rect class="front-room-return-grille" x="715" y="233" width="43" height="24" rx="2" />
                  <line v-for="line in 5" :key="`return-${line}`" x1="721" :y1="234 + line * 4" x2="752" :y2="234 + line * 4" class="front-room-return-slat" />
                  <circle v-for="dot in 5" :key="`return-dot-${dot}`" class="front-room-return-dot" cx="700" cy="245" r="3" :style="{ '--dot-index': dot }" />
                </g>

                <g class="front-room-partition" aria-label="空调主机区与房间模型区分隔墙">
                  <polygon points="520,22 512,78 512,300 520,404" mask="url(#zone-partition-mask)" />
                  <g mask="url(#zone-partition-mask)">
                    <path class="front-room-partition-light-edge" d="M520 22L512 78V300L520 404" />
                    <path class="front-room-partition-dark-edge" d="M516 50L518 402" />
                  </g>
                  <rect class="front-room-sleeve supply" x="499" y="182" width="34" height="21" rx="4" />
                  <rect class="front-room-sleeve return" x="499" y="237" width="34" height="19" rx="4" />
                  <g class="front-room-sleeve-fasteners supply">
                    <circle cx="503" cy="186" r="1.5" /><circle cx="529" cy="186" r="1.5" /><circle cx="503" cy="199" r="1.5" /><circle cx="529" cy="199" r="1.5" />
                  </g>
                  <g class="front-room-sleeve-fasteners return">
                    <circle cx="503" cy="241" r="1.5" /><circle cx="529" cy="241" r="1.5" /><circle cx="503" cy="252" r="1.5" /><circle cx="529" cy="252" r="1.5" />
                  </g>
                </g>

                <g class="front-room-ddc" transform="translate(74 -103)">
                  <rect x="176" y="218" width="50" height="83" rx="2" />
                  <text x="186" y="239">DDC-01</text><text class="front-room-ddc-model" x="184" y="257">PCS200</text>
                  <circle :class="{ active: running }" cx="201" cy="274" r="4" /><text x="184" y="291">AUTO</text>
                  <path d="M226 274H240" />
                </g>
                <path class="front-room-ddc-link" d="M314 171V208H240V218" />

                <g class="front-room-ahu" transform="translate(-60 0)" filter="url(#front-ahu-shadow)" aria-label="空调主机设备区内的一套新风空调机组">
                  <polygon class="front-room-ahu-top" points="240,236 264,218 544,218 520,236" />
                  <polygon class="front-room-ahu-side" points="520,236 544,218 544,318 520,336" />
                  <rect class="front-room-ahu-face" x="240" y="236" width="280" height="100" rx="2" />
                  <line class="front-room-ahu-highlight" x1="245" y1="239" x2="515" y2="239" />
                  <line x1="333" y1="260" x2="333" y2="336" /><line x1="426" y1="260" x2="426" y2="336" />
                  <rect class="front-room-ahu-tag" x="250" y="245" width="55" height="16" rx="2" />
                  <text class="front-room-ahu-tag-text" x="258" y="257">FAHU-01</text>
                  <text class="front-room-ahu-name" x="315" y="257">组合式新风空调机组 · {{ modeLabel }}</text>
                  <g class="front-room-filter">
                    <rect x="266" y="278" width="42" height="31" /><path d="M270 304L289 282L304 304M278 307L299 282" />
                    <text x="269" y="326">过滤段</text>
                  </g>
                  <g class="front-room-coil">
                    <rect x="357" y="278" width="44" height="31" />
                    <line x1="364" y1="282" x2="364" y2="305" /><line x1="372" y1="282" x2="372" y2="305" />
                    <line x1="380" y1="282" x2="380" y2="305" /><line x1="388" y1="282" x2="388" y2="305" />
                    <line x1="396" y1="282" x2="396" y2="305" />
                    <text x="355" y="326">{{ state.mode === 'heating' ? '加热盘管' : '表冷盘管' }}</text>
                  </g>
                  <g class="front-room-fan">
                    <circle cx="472" cy="294" r="21" />
                    <g class="front-room-fan-rotor">
                      <path d="M472 291C476 277 486 278 488 283C490 289 480 294 472 294Z" />
                      <path d="M475 294C489 298 488 308 483 310C477 312 472 302 472 294Z" />
                      <path d="M472 297C468 311 458 310 456 305C454 299 464 294 472 294Z" />
                      <path d="M469 294C455 290 456 280 461 278C467 276 472 286 472 294Z" />
                    </g>
                    <text x="446" y="326">送风机 {{ state.fanFrequency.toFixed(1) }}Hz</text>
                  </g>
                  <g class="front-room-ahu-hardware">
                    <circle cx="248" cy="268" r="1.8" /><circle cx="325" cy="268" r="1.8" />
                    <circle cx="341" cy="268" r="1.8" /><circle cx="418" cy="268" r="1.8" />
                    <circle cx="434" cy="268" r="1.8" /><circle cx="512" cy="268" r="1.8" />
                    <rect x="323" y="286" width="4" height="15" rx="2" />
                    <rect x="416" y="286" width="4" height="15" rx="2" />
                  </g>
                  <line class="front-room-ahu-base" x1="242" y1="332" x2="518" y2="332" />
                  <rect class="front-room-ahu-foot" x="262" y="336" width="20" height="7" /><rect class="front-room-ahu-foot" x="478" y="336" width="20" height="7" />
                </g>
                <g class="front-room-ahu-flanges" aria-hidden="true">
                  <rect class="supply" x="477" y="234" width="14" height="20" rx="2" />
                  <rect class="return" x="477" y="259" width="14" height="18" rx="2" />
                </g>

                <g class="front-room-sensor front-room-sensor-supply" aria-label="SA-TH-01 送风温湿度传感器">
                  <rect x="778" y="80" width="170" height="40" rx="3" />
                  <text class="front-room-sensor-id" x="788" y="95">SA-TH-01</text>
                  <text class="front-room-sensor-label" x="842" y="95">送风温湿度</text>
                  <text class="front-room-sensor-value" x="788" y="114">{{ state.supplyTemp.toFixed(1) }}℃ · {{ state.supplyHumidity.toFixed(1) }}%RH</text>
                </g>
                <g class="front-room-sensor front-room-sensor-temp" aria-label="TE-01 回风温度传感器">
                  <rect x="778" y="126" width="82" height="60" rx="3" />
                  <text class="front-room-sensor-id" x="788" y="142">TE-01</text>
                  <text class="front-room-sensor-label" x="788" y="157">回风温度</text>
                  <text class="front-room-sensor-value" x="788" y="178">{{ latest.indoorTemp.toFixed(1) }}℃</text>
                </g>
                <g class="front-room-sensor front-room-sensor-co2" aria-label="CE-01 回风二氧化碳传感器">
                  <rect x="866" y="126" width="82" height="60" rx="3" />
                  <text class="front-room-sensor-id" x="876" y="142">CE-01</text>
                  <text class="front-room-sensor-label" x="876" y="157">回风 CO₂ 含量</text>
                  <text class="front-room-sensor-value" x="876" y="178">{{ latest.co2 }}ppm</text>
                </g>

                <g class="front-room-people" clip-path="url(#conditioned-floor-clip)">
                  <g v-for="person in people" :key="person" class="front-room-person" :transform="`translate(${630 + (person % 4) * 67} ${324 + person * 8}) scale(${0.82 + person * 0.045})`">
                    <ellipse cx="0" cy="26" rx="13" ry="4" /><circle cx="0" cy="0" r="7" /><path d="M-9 11Q0 4 9 11L11 31H-11Z" />
                  </g>
                </g>
              </svg>
            </div>
            <div class="scene-legend"><span><i class="fresh"></i>温控送风</span><span><i class="return"></i>回风</span><span><i class="control"></i>DDC 控制</span></div>
          </div>
        </section>

        <div class="simulation-analysis-row">
          <section class="pid-tuning-panel" aria-label="PID参数设计">
            <header><div><span>PID 参数设置</span><small>TE-01 回风温度环 · 输入数值实时调节</small></div><b>PV - SP</b></header>
            <div class="pid-value-grid">
              <div><span>目标值</span><strong>{{ TEMPERATURE_TARGET.toFixed(1) }}℃</strong></div>
              <div><span>设定温度 SP</span><strong>{{ inputs.setpoint.toFixed(1) }}℃</strong></div>
              <div><span>实际温度 PV</span><strong>{{ latest.indoorTemp.toFixed(2) }}℃</strong></div>
              <div><span>控制死区</span><strong>±{{ PID_TUNING.integralDeadband.toFixed(2) }}℃</strong></div>
              <div class="pid-error-value"><span>当前偏差</span><strong>{{ pidErrorLabel }}</strong></div>
              <div><span>PID 输出</span><strong>{{ pidOutputLabel }}</strong></div>
            </div>
            <label class="pid-setting"><span>P · 比例增益 <small>0.00–1.00</small></span><input :value="inputs.pidKp" type="number" min="0" max="1" step="0.01" inputmode="decimal" aria-label="PID比例增益P" @input="updatePidValue('pidKp', $event)" @change="normalizePidValue('pidKp', $event)" /></label>
            <label class="pid-setting"><span>I · 积分增益 <small>0.000–0.200</small></span><input :value="inputs.pidKi" type="number" min="0" max="0.2" step="0.001" inputmode="decimal" aria-label="PID积分增益I" @input="updatePidValue('pidKi', $event)" @change="normalizePidValue('pidKi', $event)" /></label>
            <label class="pid-setting"><span>D · 微分增益 <small>0.00–0.50</small></span><input :value="inputs.pidKd" type="number" min="0" max="0.5" step="0.01" inputmode="decimal" aria-label="PID微分增益D" @input="updatePidValue('pidKd', $event)" @change="normalizePidValue('pidKd', $event)" /></label>
          </section>

          <section class="simulation-trend-card">
          <header><div><span>04 / TREND</span><strong>环境趋势与稳定性诊断</strong></div><small>最近 {{ trend.length }} 个采样点</small></header>
          <div class="simulation-trend-grid">
            <div class="trend-chart">
              <div class="trend-axis"><span>{{ temperatureScale.max.toFixed(1) }}℃</span><span>{{ temperatureScale.min.toFixed(1) }}℃</span></div>
              <div class="trend-axis trend-axis-right"><span>{{ Math.round(co2Scale.max) }}ppm</span><span>{{ Math.round(co2Scale.min) }}ppm</span></div>
              <svg viewBox="0 0 100 80" preserveAspectRatio="none" role="img" aria-label="室温与二氧化碳趋势图">
                <line v-for="line in [18, 36, 54, 72]" :key="line" x1="0" :y1="line" x2="100" :y2="line" />
                <line v-if="temperatureScale.min <= inputs.setpoint && temperatureScale.max >= inputs.setpoint" class="trend-reference" x1="0" :y1="74 - ((inputs.setpoint - temperatureScale.min) / (temperatureScale.max - temperatureScale.min)) * 62" x2="100" :y2="74 - ((inputs.setpoint - temperatureScale.min) / (temperatureScale.max - temperatureScale.min)) * 62" />
                <polyline class="trend-temp" :points="chartPoints('indoorTemp', temperatureScale.min, temperatureScale.max)" />
                <polyline class="trend-co2" :points="chartPoints('co2', co2Scale.min, co2Scale.max)" />
              </svg>
              <div class="trend-time-axis"><span>{{ trendTimeRange.start.toFixed(1) }}min</span><span>{{ trendTimeRange.end.toFixed(1) }}min</span></div>
              <div class="trend-key"><span><i class="setpoint"></i>设定温度 {{ inputs.setpoint.toFixed(1) }}℃</span><span><i class="temp"></i>实际温度 {{ latest.indoorTemp.toFixed(1) }}℃</span><span><i class="co2"></i>CO₂</span></div>
            </div>
            <div class="stability-grid">
              <article :class="{ stable: stability.ready && stability.tempStable, warning: stability.ready && !stability.tempStable }">
                <span>温度稳定性</span><strong>{{ stability.ready ? `${stability.tempRate >= 0 ? '+' : ''}${stability.tempRate.toFixed(3)}℃/min` : '采样中…' }}</strong><small>{{ stability.ready && stability.tempStable ? '已进入稳定区间' : '系统正在消除温差' }}</small>
              </article>
              <article :class="{ stable: stability.ready && stability.co2Stable, warning: stability.ready && !stability.co2Stable }">
                <span>CO₂ 稳定性</span><strong>{{ stability.ready ? `${stability.co2Rate >= 0 ? '+' : ''}${stability.co2Rate.toFixed(2)}ppm/min` : '采样中…' }}</strong><small>{{ stability.ready && stability.co2Stable ? '新风量与负荷平衡' : '持续计算新风需求' }}</small>
              </article>
            </div>
          </div>
          </section>
        </div>
      </main>

      <aside class="simulation-live-panel">
        <div class="simulation-panel-title"><span>03</span><div><strong>实时点位</strong><small>SIMULATION ENGINE</small></div></div>
        <div class="simulation-kpis">
          <article><span>回风温度 TE-01</span><strong data-testid="return-air-temperature">{{ latest.indoorTemp.toFixed(2) }}<small>℃</small></strong><small class="simulation-kpi-note">设定 {{ inputs.setpoint.toFixed(1) }}℃</small><i :style="{ width: `${Math.min(100, latest.indoorTemp / 35 * 100)}%` }"></i></article>
          <article><span>CO₂ 浓度</span><strong>{{ latest.co2 }}<small>ppm</small></strong><i :style="{ width: `${Math.min(100, latest.co2 / 1600 * 100)}%` }"></i></article>
          <article><span>相对湿度</span><strong>{{ latest.humidity.toFixed(1) }}<small>%RH</small></strong><i :style="{ width: `${latest.humidity}%` }"></i></article>
          <article class="comfort-score">
            <span>舒适度评分 <small class="comfort-grade">{{ comfortMeta.label }}</small></span>
            <strong>{{ latest.comfort.toFixed(1) }}<small>/100</small></strong>
            <small class="simulation-kpi-note">{{ comfortIssue }}</small>
            <i :style="{ width: `${latest.comfort}%` }"></i>
          </article>
          <div class="comfort-factor-strip" aria-label="舒适度分项：温度权重40%，二氧化碳权重30%，湿度权重30%">
            <div v-for="factor in comfortFactors" :key="factor.key" :class="{ weakest: latest.comfort < 99.9 && factor.key === weakestComfortFactor.key }">
              <span>{{ factor.label }}</span><strong>{{ Math.round(factor.score) }}</strong>
            </div>
          </div>
        </div>

        <div class="ahu-points">
          <div class="ahu-point-primary">
            <div class="ahu-point-primary-heading"><span>当前总送风量</span><small>设计上限 {{ designAirflows.maximumSupplyFlow }}</small></div>
            <strong data-testid="supply-airflow">{{ Math.round(state.supplyFlow) }}<small>m³/h</small></strong>
            <div class="ahu-point-primary-meta"><span>实际新风 {{ Math.round(state.outdoorAirFlow) }} m³/h</span><span>回风 {{ Math.round(state.returnAirFlow) }} m³/h</span></div>
            <div class="ahu-point-progress" role="progressbar" aria-label="送风机频率负载" aria-valuemin="0" aria-valuemax="100" :aria-valuenow="Math.round(supplyPercentage)"><i :style="{ width: `${supplyPercentage}%` }"></i></div>
          </div>
          <div class="ahu-point-mini"><span>送风机变频</span><strong data-testid="supply-fan-frequency">{{ state.fanFrequency.toFixed(1) }}<small>Hz</small></strong></div>
          <div class="ahu-point-mini"><span>新风阀 / 换气</span><strong>{{ Math.round(state.damperPosition) }}<small>% · {{ state.airChanges.toFixed(1) }}次/h</small></strong></div>
          <div class="ahu-point-mini"><span>{{ waterSourceLabel }}阀开度</span><strong data-testid="water-valve-position">{{ state.waterValve.toFixed(1) }}<small>%</small></strong></div>
          <div class="ahu-point-secondary">
            <span>机组状态 <strong data-testid="ahu-mode">{{ modeLabel }}</strong></span>
            <span>估算功率 <strong>{{ state.energyKw.toFixed(1) }} kW</strong></span>
          </div>
        </div>

      </aside>

      <section class="simulation-strategy simulation-strategy-card">
        <span>控制策略</span>
        <strong v-if="!inputs.acEnabled">机组运行许可已关闭；送风机停机、水阀关闭，PID 状态已复位。</strong>
        <strong v-else-if="state.mode === 'cooling'">TE-01 回风温度主 PID 连续调节冷水阀，并联动重置送风机频率。</strong>
        <strong v-else-if="state.mode === 'heating'">TE-01 回风温度主 PID 连续调节热水阀，并联动重置送风机频率。</strong>
        <strong v-else>运行许可有效时送风机持续运行，回风温度到达设定值不会触发停机。</strong>
        <small data-testid="pid-status">控制对象 TE-01 回风温度 {{ latest.indoorTemp.toFixed(2) }}℃ · SP {{ inputs.setpoint.toFixed(1) }}℃ · PID 输出 {{ pidOutputLabel }}</small>
        <small>Kp {{ inputs.pidKp.toFixed(2) }} · Ki {{ inputs.pidKi.toFixed(3) }} · Kd {{ inputs.pidKd.toFixed(2) }} · 偏差 {{ pidErrorLabel }} · 当前最低频率 {{ fanFloor.toFixed(1) }}Hz · PID 负荷 {{ pidPercentage.toFixed(1) }}%</small>
        <small>两管制当前为{{ waterSourceLabel }}许可；切换时水阀安全关闭并复位 PID，现场安全联锁拥有最高优先级。</small>
        <small class="comfort-method-note">舒适度：温度 40% · CO₂ 30% · 湿度 30%；新风由人数和面积联动，且始终不超过总送风。</small>
      </section>
    </div>
  </section>
</template>
